import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPlantEmoji } from '../data/plantEmojis';
import { getPublicCoupons, validateCoupon, getShippingSettings } from '../lib/api';
import './CartDrawer.css';

/* ── Icons ── */
function TagIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );
}
function CloseIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function CheckCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  );
}

/* ── Coupon Modal (View All) ── */
function CouponModal({ open, onClose, onApply, appliedCoupon, total }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getPublicCoupons()
      .then(data => setCoupons(Array.isArray(data) ? data : []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  return (
    <div className="cd-coupon-overlay" onClick={onClose}>
      <div className="cd-coupon-modal" onClick={e => e.stopPropagation()}>
        <div className="cd-coupon-modal__header">
          <div className="cd-coupon-modal__title"><TagIcon /> Available Coupons</div>
          <button className="cd-coupon-modal__close" onClick={onClose}><CloseIcon size={16} /></button>
        </div>

        <div className="cd-coupon-modal__body">
          {loading && <p className="cd-coupon-empty">Loading coupons…</p>}
          {!loading && coupons.length === 0 && (
            <p className="cd-coupon-empty">No coupons available right now.</p>
          )}
          {!loading && coupons.map(c => {
            const isApplied = appliedCoupon?.code === c.code;
            const eligible  = total >= (c.minOrder || 0);
            const expiring  = c.expiresAt && (() => {
              const days = Math.ceil((new Date(c.expiresAt) - Date.now()) / 86400000);
              return days <= 7 ? `Expires in ${days} day${days === 1 ? '' : 's'}` : null;
            })();
            return (
              <div
                key={c.code}
                className={`cd-coupon-card${isApplied ? ' cd-coupon-card--applied' : ''}${!eligible ? ' cd-coupon-card--locked' : ''}`}
              >
                <div className="cd-coupon-card__left">
                  <div className="cd-coupon-card__tag">
                    <TagIcon />
                    <span>{c.code}</span>
                    {expiring && <span className="cd-coupon-card__expiry">{expiring}</span>}
                  </div>
                  <div className="cd-coupon-card__label">{c.label}</div>
                  {c.desc && <div className="cd-coupon-card__desc">{c.desc}</div>}
                  {c.hasItemRestrictions && (
                    <div className="cd-coupon-card__restriction">Valid on selected items only</div>
                  )}
                  {c.minOrder > 0 && (
                    <div className={`cd-coupon-card__min${eligible ? ' cd-coupon-card__min--ok' : ''}`}>
                      {eligible
                        ? '✓ Eligible on your order'
                        : <><LockIcon /> Min. order ₹{c.minOrder.toLocaleString()}</>}
                    </div>
                  )}
                </div>
                <button
                  className={`cd-coupon-card__btn${isApplied ? ' cd-coupon-card__btn--applied' : ''}${!eligible ? ' cd-coupon-card__btn--locked' : ''}`}
                  disabled={!eligible}
                  onClick={() => { if (eligible) { onApply(c.code); onClose(); } }}
                >
                  {isApplied ? 'Applied ✓' : eligible ? 'Apply' : 'Locked'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Coupon Section ── */
function CouponSection({ total, appliedCoupon, setAppliedCoupon }) {
  const [code, setCode]           = useState('');
  const [applying, setApplying]   = useState(false);
  const [error, setError]         = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  // Sync input with applied coupon
  useEffect(() => {
    if (appliedCoupon) setCode(appliedCoupon.code);
    else setCode('');
  }, [appliedCoupon]);

  const handleApply = async (overrideCode) => {
    const c = (overrideCode || code).trim().toUpperCase();
    if (!c) return;
    setApplying(true);
    setError('');
    try {
      const result = await validateCoupon(c, total);
      setAppliedCoupon({ ...result, desc: result.desc || '' });
      setCode(result.code);
    } catch (err) {
      setError(err.message || 'Invalid coupon code.');
      setAppliedCoupon(null);
    } finally {
      setApplying(false);
    }
  };

  const handleRemove = () => {
    setAppliedCoupon(null);
    setCode('');
    setError('');
  };

  return (
    <div className="cd-coupon">
      {/* Input row — hidden once a coupon is applied */}
      {!appliedCoupon && (
        <div className="cd-coupon__row">
          <div className="cd-coupon__input-wrap">
            <TagIcon />
            <input
              className="cd-coupon__input"
              placeholder="Enter coupon code"
              value={code}
              onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleApply()}
            />
          </div>
          <button
            className="cd-coupon__apply-btn"
            onClick={() => handleApply()}
            disabled={applying || !code.trim()}
          >
            {applying ? '…' : 'Apply'}
          </button>
        </div>
      )}

      {/* Error */}
      {error && <p className="cd-coupon__error">{error}</p>}

      {/* Applied success strip */}
      {appliedCoupon && (
        <div className="cd-coupon__success">
          <span>🎉 <strong>{appliedCoupon.code}</strong> — saving ₹{appliedCoupon.discountAmount?.toLocaleString()}</span>
          <button className="cd-coupon__remove-link" onClick={handleRemove}>Remove</button>
        </div>
      )}

      {/* View all link */}
      <button className="cd-coupon__view-all" onClick={() => setModalOpen(true)}>
        <TagIcon /> View all coupons
      </button>

      <CouponModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onApply={(c) => handleApply(c)}
        appliedCoupon={appliedCoupon}
        total={total}
      />
    </div>
  );
}

/* ── Free Delivery Banner ── */

function DeliveryBanner({ total, threshold }) {
  // threshold === null means free shipping is disabled entirely — say nothing rather than mislead.
  if (threshold === null) return null;
  const remaining = threshold - total;
  if (remaining <= 0) {
    return (
      <div className="cart-drawer__delivery-banner cart-drawer__delivery-banner--achieved">
        <span>You've got <strong>Free Delivery</strong>!</span>
      </div>
    );
  }
  return (
    <div className="cart-drawer__delivery-banner">
      <span>Add <strong>₹{remaining.toLocaleString()}</strong> more to get <strong>Free Delivery</strong></span>
    </div>
  );
}

/* ── Main CartDrawer ── */
export default function CartDrawer({ open, onClose, cart, updateQty, removeFromCart, total, appliedCoupon, setAppliedCoupon }) {
  const navigate = useNavigate();
  const [shippingSettings, setShippingSettings] = useState(null);

  useEffect(() => {
    getShippingSettings().then(setShippingSettings).catch(() => {});
  }, []);

  const freeShippingThreshold = shippingSettings
    ? (shippingSettings.freeShippingEnabled ? shippingSettings.freeShippingThreshold : null)
    : 999; // sane default while loading

  const discount   = appliedCoupon?.discountAmount || 0;
  const finalTotal = Math.max(0, total - discount);

  const goToCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      <div className={`cart-overlay${open ? ' cart-overlay--open' : ''}`} onClick={onClose} />
      <aside className={`cart-drawer${open ? ' cart-drawer--open' : ''}`}>
        {/* Header */}
        <div className="cart-drawer__header">
          <h2 className="cart-drawer__title">CART</h2>
          <button className="cart-drawer__close" onClick={onClose} aria-label="Close cart">
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Free Delivery Banner — always visible when cart has items */}
        {cart.length > 0 && <DeliveryBanner total={total} threshold={freeShippingThreshold} />}

        {cart.length === 0 ? (
          <div className="cart-drawer__empty">
            <div className="cart-drawer__empty-icon">🌱</div>
            <p className="cart-drawer__empty-title">Your cart is empty</p>
            <p className="cart-drawer__empty-sub">Add some plants to get started</p>
            <button className="cart-drawer__shop-btn" onClick={onClose}>Browse Plants</button>
          </div>
        ) : (
          <>
            <div className="cart-drawer__items">
              {cart.map(item => (
                <div className="cart-item" key={item.cartItemId || item.id}>
                  {(() => {
                    const itemSlug = item.slug;
                    const itemVariant = item._variantId;
                    const itemHref = itemSlug
                      ? `/product/${itemSlug}${itemVariant ? `?variant=${itemVariant}` : ''}`
                      : null;
                    const ImgWrapper = itemHref
                      ? ({ children }) => <Link to={itemHref} onClick={onClose} className="cart-item__img cart-item__img--link" tabIndex={-1} aria-label={`View ${item.name}`}>{children}</Link>
                      : ({ children }) => <div className="cart-item__img">{children}</div>;
                    return (
                      <ImgWrapper>
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="cart-item__img-photo"
                            onError={e => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span
                          className="cart-item__emoji"
                          style={{ display: item.imageUrl ? 'none' : 'flex' }}
                        >
                          {getPlantEmoji(item.name)}
                        </span>
                      </ImgWrapper>
                    );
                  })()}

                  <div className="cart-item__info">
                    <div className="cart-item__top-row">
                      {item.slug ? (
                        <Link
                          to={`/product/${item.slug}${item._variantId ? `?variant=${item._variantId}` : ''}`}
                          onClick={onClose}
                          className="cart-item__name cart-item__name--link"
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <p className="cart-item__name">{item.name}</p>
                      )}
                      <button className="cart-item__remove" onClick={() => removeFromCart(item.cartItemId || item.id)} aria-label="Remove">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6"/><path d="M14 11v6"/>
                        </svg>
                      </button>
                    </div>
                    {(item._selectedSize || item._selectedPlanter || item._selectedColor) && (
                      <p className="cart-item__variants">
                        {[item._selectedSize, item._selectedPlanter, item._selectedColor].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {/* Bundle: show selected plants */}
                    {item.type === 'bundle' && item.bundleSelectedProducts?.length > 0 && (
                      <div className="cart-bundle-items">
                        {item.bundleSelectedProducts.map((p, pi) => {
                          const variants = [p.sizeLabel, p.planterName, p.colorLabel].filter(Boolean).join(' · ');
                          return (
                            <div key={pi} className="cart-bundle-item">
                              {p.imageUrl
                                ? <img src={p.imageUrl} alt={p.name} className="cart-bundle-item__img" />
                                : <span className="cart-bundle-item__fallback">🌿</span>
                              }
                              <span className="cart-bundle-item__info">
                                <span className="cart-bundle-item__name">{p.name}</span>
                                {variants && <span className="cart-bundle-item__variant">{variants}</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="cart-item__bottom-row">
                      {item.type === 'bundle' ? (
                        <span style={{ fontSize: '.78rem', color: '#6b7280' }}>Bundle · qty 1</span>
                      ) : (
                        <div className="cart-item__qty">
                          <button onClick={() => updateQty(item.cartItemId || item.id, item.qty - 1)}>−</button>
                          <span>{item.qty}</span>
                          <button onClick={() => updateQty(item.cartItemId || item.id, item.qty + 1)}>+</button>
                        </div>
                      )}
                      <p className="cart-item__price">₹{(item.price * item.qty).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-drawer__footer">
              {/* Coupon Section */}
              <CouponSection
                total={total}
                appliedCoupon={appliedCoupon}
                setAppliedCoupon={setAppliedCoupon}
              />

              {/* Total + Checkout */}
              <div className="cart-drawer__total-row">
                <div className="cart-drawer__total-label">
                  <span>Total</span>
                  {discount > 0 && (
                    <span className="cart-drawer__total-savings">You save ₹{discount.toLocaleString()}</span>
                  )}
                </div>
                <span className="cart-drawer__total-amount">₹{finalTotal.toLocaleString()}</span>
              </div>

              <button className="cart-drawer__checkout" onClick={goToCheckout}>Proceed to Checkout →</button>
              <div className="cart-drawer__footer-links">
                <Link to="/cart" className="cart-drawer__view-cart" onClick={onClose}>View full cart</Link>
                <button className="cart-drawer__continue" onClick={onClose}>Continue Shopping</button>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}